import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { getTripNicSync } from '@/services/sync';
import Loader from '@/components/ui/loader';
import { formatTableText } from '@/lib/tableUtils';

const formatText = (text) => {
  if (!text) return 'N/A';
  const str = String(text).replace(/_/g, ' ');
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const getStatusVariant = (status) => {
  const s = String(status || '').toLowerCase();
  if (s.includes('success')) return 'active';
  if (s.includes('skip')) return 'scheduled';
  if (s.includes('fail') || s.includes('error')) return 'inactive';
  return 'default';
};

const formatReason = (responseJson) => {
  if (!responseJson) return 'N/A';
  if (typeof responseJson === 'string') return formatText(responseJson);
  const key = responseJson.reason || responseJson.error || responseJson.message || responseJson.sign_error;
  if (key) return formatText(key);
  return 'N/A';
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  } catch {
    return dateString;
  }
};

const CustomDashboardCard = ({ name, count, unit = '' }) => {
  if (!count || count === 'N/A' || count === 'null' || count === 'undefined') return null;
  return (
    <div
      className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all"
      style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}
    >
      <div className="border border-border px-3 py-2 rounded-[16px] bg-background">
        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground">
            {name}
          </div>
          <div className="text-xs font-normal break-all">
            {count} {unit}
          </div>
        </div>
      </div>
    </div>
  );
};

export const NicSyncDialog = ({ open, onOpenChange, trip }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSyncData = useCallback(async () => {
    if (!trip?.id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getTripNicSync(trip.id);
      const data = response?.data || (Array.isArray(response) ? response : []);
      setLogs(data);
    } catch (err) {
      const msg = err?.message || '';
      if (msg.includes('404')) {
        setLogs([]);
        setError(null);
      } else if (msg.includes('500') || msg.includes('502') || msg.includes('503')) {
        setError('Server is temporarily unavailable to fetch NIC sync records. Please try again later.');
        setLogs([]);
      } else {
        setError('Unable to load NIC sync details at this time. Please try again later.');
        setLogs([]);
      }
    } finally {
      setLoading(false);
    }
  }, [trip?.id]);

  useEffect(() => {
    if (open && trip?.id) {
      fetchSyncData();
    } else {
      setLogs([]);
      setError(null);
    }
  }, [open, trip?.id, fetchSyncData]);

  const vehicleVal = trip?.truck || trip?.vehicle || trip?.vehicle_number || '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[92vw] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">NIC Sync Details</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center items-center h-48">
            <Loader size="md" message="Loading sync details..." />
          </div>
        ) : error ? (
          <div className="space-y-4 pt-1">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              <CustomDashboardCard
                name="Trip Number"
                count={trip?.tripNumber || trip?.trip_number}
              />
              {vehicleVal && vehicleVal !== 'N/A' && (
                <CustomDashboardCard
                  name="Vehicle Number"
                  count={vehicleVal}
                />
              )}
              <CustomDashboardCard
                name="Trip Status"
                count={formatText(trip?.status)}
              />
            </div>

            <div
              className="border border-border p-0.5 rounded-[16px] bg-card w-full"
              style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}
            >
              <div className="border border-border px-4 py-8 rounded-[16px] bg-background text-center text-xs text-muted-foreground">
                {error}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 pt-1">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              <CustomDashboardCard
                name="Trip Number"
                count={trip?.tripNumber || trip?.trip_number}
              />
              {vehicleVal && vehicleVal !== 'N/A' && (
                <CustomDashboardCard
                  name="Vehicle Number"
                  count={vehicleVal}
                />
              )}
              <CustomDashboardCard
                name="Trip Status"
                count={formatText(trip?.status)}
              />
            </div>

            {logs.length === 0 ? (
              <div
                className="border border-border p-0.5 rounded-[16px] bg-card w-full"
                style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}
              >
                <div className="border border-border px-4 py-8 rounded-[16px] bg-background text-center text-xs text-muted-foreground">
                  No sync records found for this trip.
                </div>
              </div>
            ) : (
              <div
                className="border border-border p-0.5 rounded-[16px] bg-card w-full"
                style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}
              >
                <div className="border border-border p-2 rounded-[16px] bg-background">
                  <div className="rounded-md border overflow-hidden w-full">
                    <Table>
                      <TableHeader className="bg-muted/50">
                        <TableRow className="border-border">
                          <TableHead className="h-9 px-3 py-1 text-xs font-medium text-center min-w-[90px]">Status</TableHead>
                          <TableHead className="h-9 px-3 py-1 text-xs font-medium">Reason</TableHead>
                          <TableHead className="h-9 px-3 py-1 text-xs font-medium">Invoice No</TableHead>
                          <TableHead className="h-9 px-3 py-1 text-xs font-medium">dispatch Id</TableHead>
                          <TableHead className="h-9 px-3 py-1 text-xs font-medium">Date & Time</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {logs.map((log, index) => (
                          <TableRow key={log.id || index} className="hover:bg-muted/30 border-border">
                            <TableCell className="h-9 px-3 py-1 text-xs text-center">
                              <Badge
                                variant={getStatusVariant(log.status)}
                                className="inline-block text-center px-2 py-0.5 min-w-[75px] text-[11px]"
                              >
                                {formatText(log.status)}
                              </Badge>
                            </TableCell>
                            <TableCell className="h-9 px-3 py-1 text-xs font-normal text-foreground">
                              {formatReason(log.responseJson)}
                            </TableCell>
                            <TableCell className="h-9 px-3 py-1 text-xs font-normal">
                              {formatTableText(log.invoiceNumber) || 'N/A'}
                            </TableCell>
                            <TableCell className="h-9 px-3 py-1 text-xs font-normal">
                              {formatTableText(log.requestJson?.dispatchId || log.requestJson?.tripStartEndList?.dispatchId) || 'N/A'}
                            </TableCell>
                            <TableCell className="h-9 px-3 py-1 text-xs font-normal text-muted-foreground whitespace-nowrap">
                              {formatDateTime(log.createdAt)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NicSyncDialog;
